// Marketing website translations - Dutch, English, German
export const marketingTranslations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      features: 'Features',
      pricing: 'Pricing',
      about: 'About',
      contact: 'Contact',
      getStarted: 'Get Started',
      login: 'Login'
    },
    // Hero Section
    hero: {
      title: 'Take Control of Your Business',
      subtitle: 'The complete operating system for your hospitality business. Streamline operations, manage daily tasks, control margins, and strengthen your team.',
      cta: 'Start Free Trial',
      secondaryCta: 'See Features',
      tagline: 'Control the chaos in hospitality'
    },
    // Features Overview
    featuresOverview: {
      title: 'Everything You Need to Run Your Business',
      subtitle: 'From daily tasks to control over margins, Supervisor gives you full control so you can focus on running your business.',
      operations: {
        title: 'Operations',
        description: 'Streamline daily tasks with digital checklists, HACCP compliance, table reservations, and stock management.',
        items: ['Digital Checklists', 'HACCP Compliance', 'Table Reservations', 'Stock Management', 'Daily Tasks']
      },
      finance: {
        title: 'Finance',
        description: 'Get complete financial oversight with cash counts, margin tracking, cost calculations, and revenue forecasting.',
        items: ['Cash Counts', 'Profit Margins', 'Cost Calculations', 'Revenue Forecast', 'Financial Reports']
      },
      employees: {
        title: 'Employee Management',
        description: 'Manage your team effectively with role-based permissions, performance reviews, and task assignments.',
        items: ['Role-Based Access', 'Performance Reviews', 'Task Management', 'Team Communication']
      },
      multiBranch: {
        title: 'Multi-Branch Support',
        description: 'Manage multiple locations from one dashboard. Perfect for growing hospitality businesses.',
        items: ['Centralized Dashboard', 'Branch Comparison', 'Unified Reporting', 'Location Management']
      }
    },
    // Features Page
    features: {
      title: 'Powerful Features for Hospitality',
      subtitle: 'Discover all the tools that make Supervisor the perfect partner for your business.',
      operations: {
        title: 'Operations Management',
        description: 'Keep your business running smoothly with comprehensive operational tools.',
        checklists: {
          title: 'Digital Checklists',
          description: 'Create startup and shutdown checklists. Track completion and ensure nothing is missed.'
        },
        haccp: {
          title: 'HACCP Compliance',
          description: 'Stay compliant with food safety regulations. Log temperatures, deliveries, and hygiene controls.'
        },
        reservations: {
          title: 'Table Reservations',
          description: 'Visual floor plan management. Drag-and-drop table layout with real-time availability.'
        },
        stock: {
          title: 'Stock Management',
          description: 'Track inventory levels, manage products, and create stock lists for easy counting.'
        },
        dailyTasks: {
          title: 'Daily Tasks',
          description: 'Assign and track daily to-dos. Get alerts for pending items.'
        }
      },
      finance: {
        title: 'Financial Control',
        description: 'Complete visibility into your financial performance.',
        cashCounts: {
          title: 'Cash Counts',
          description: 'Daily cash register counts with variance tracking and historical comparison.'
        },
        margins: {
          title: 'Profit Margins',
          description: 'Track employee percentages and profit margins per service period.'
        },
        costs: {
          title: 'Cost Calculations',
          description: 'Calculate dish costs, manage cost items, and optimize pricing.'
        },
        forecast: {
          title: 'Revenue Forecast',
          description: 'Predict future income based on historical data and trends.'
        },
        products: {
          title: 'Product Management',
          description: 'Manage your product catalog, track inventory, and monitor stock levels.'
        },
        costPrice: {
          title: 'Cost Price Calculation',
          description: 'Calculate the true cost price of your products and dishes for optimal pricing.'
        }
      },
      team: {
        title: 'Team Management',
        description: 'Empower your team while maintaining control.',
        permissions: {
          title: 'Role-Based Permissions',
          description: 'Create custom permission profiles. Control who sees what and who can do what.'
        },
        reviews: {
          title: 'Performance Reviews',
          description: 'Track employee performance with structured review templates.'
        },
        communication: {
          title: 'Team Communication',
          description: 'Weekly updates, messages, and feedback channels for your team.'
        }
      },
      technical: {
        title: 'Technical Excellence',
        description: 'Built for modern hospitality businesses.',
        pwa: {
          title: 'Mobile Ready (PWA)',
          description: 'Works on any device. Install as an app on phones and tablets.'
        },
        languages: {
          title: 'Multi-Language',
          description: 'Available in Dutch, English, and German. Easy to switch per user.'
        },
        multiBranch: {
          title: 'Multi-Branch',
          description: 'Manage unlimited locations from one account. Compare and consolidate.'
        }
      }
    },
    // Pricing Page
    pricing: {
      title: 'Simple, Transparent Pricing',
      subtitle: 'Choose the plan that fits your business. No hidden fees.',
      monthly: 'per month',
      freeTrial: '3-day free trial included',
      company: {
        title: 'Company',
        description: 'For businesses managing their own operations',
        price: '49.95',
        features: [
          'Full operations suite',
          'Complete financial tools',
          'Employee management',
          'Permission profiles',
          'Multi-language support',
          'PWA mobile access',
          'Email support'
        ],
        cta: 'Continue'
      },
      branch: {
        title: 'Branch',
        description: 'For organizations managing multiple companies',
        price: '29.95',
        features: [
          'Manage multiple companies',
          'Single account access',
          'No constant login/logout',
          'Centralized oversight',
          'Cross-company reporting',
          'Mobile access',
          'Email support'
        ],
        cta: 'Continue'
      },
      faq: {
        title: 'Frequently Asked Questions',
        questions: [
          {
            q: 'What payment methods do you accept?',
            a: 'We accept all major credit cards and SEPA direct debit through our secure payment provider Stripe.'
          },
          {
            q: 'Can I cancel anytime?',
            a: 'Yes, you can cancel your subscription at any time. Your access continues until the end of the billing period.'
          },
          {
            q: 'Is there a free trial?',
            a: 'Yes! All plans come with a 3-day free trial. No credit card required to start.'
          },
          {
            q: 'Do you offer discounts for multiple branches?',
            a: 'Yes, contact our sales team for volume discounts on 5+ branches.'
          }
        ]
      }
    },
    // About Page
    about: {
      title: 'About Supervisor',
      subtitle: 'Helping hospitality businesses take control since day one.',
      story: {
        title: 'Our Story',
        content: 'Supervisor was born from a simple observation: hospitality business owners spend too much time managing chaos and not enough time growing their business. We started this company with one mission – to give business owners the insights and tools they need to truly understand what\'s happening in their company. That\'s why we\'re called Supervisor. We believe that as a company, you deserve to be in control. Our platform brings together operations, finance, and team management into one intuitive dashboard, giving you the oversight you need to make better decisions, faster. From a single restaurant to a chain of hotels, Supervisor scales with your ambitions.'
      },
      mission: {
        title: 'Our Mission',
        content: 'To empower hospitality businesses with the tools and insights they need to thrive. We believe that technology should simplify, not complicate. Every feature we build is designed to save you time and give you clarity.'
      },
      values: {
        title: 'Our Values',
        items: [
          {
            title: 'Simplicity',
            description: 'Powerful doesn\'t have to mean complicated. We design for ease of use.'
          },
          {
            title: 'Transparency',
            description: 'No hidden fees, no surprises. What you see is what you get.'
          },
          {
            title: 'Reliability',
            description: 'Your business depends on us. We take that responsibility seriously.'
          },
          {
            title: 'Innovation',
            description: 'We continuously improve based on real feedback from real businesses.'
          }
        ]
      },
      location: {
        title: 'Our Location',
        address: 'Zaailand 106',
        city: '8911 BN Leeuwarden',
        country: 'The Netherlands'
      }
    },
    // Contact Page
    contact: {
      title: 'Get in Touch',
      subtitle: 'Have questions? We\'d love to hear from you.',
      form: {
        name: 'Name',
        email: 'Email',
        company: 'Company',
        subject: 'Subject',
        message: 'Message',
        submit: 'Send Message',
        sending: 'Sending...',
        success: 'Message sent successfully! We\'ll get back to you soon.',
        error: 'Failed to send message. Please try again or email us directly.'
      },
      info: {
        title: 'Contact Information',
        email: 'Email',
        address: 'Address',
        hours: 'Business Hours',
        hoursValue: 'Monday - Friday: 9:00 - 17:00'
      }
    },
    // Testimonials
    testimonials: {
      title: 'What Our Customers Say',
      items: [
        {
          quote: 'Supervisor has transformed how we manage our restaurant chain. The financial insights alone have saved us thousands.',
          author: 'Jan de Vries',
          role: 'Owner, De Gouden Lepel',
          company: 'Restaurant Group'
        },
        {
          quote: 'Finally, a system that our entire team can use. The HACCP compliance features give us peace of mind every day.',
          author: 'Maria Schmidt',
          role: 'Operations Manager',
          company: 'Hotel de gouden leeuw'
        },
        {
          quote: 'The multi-branch overview is exactly what we needed. Now I can see all locations at a glance.',
          author: 'Thomas Bakker',
          role: 'CEO',
          company: 'Café Collectief'
        }
      ]
    },
    // Footer
    footer: {
      description: 'The complete operating system for your hospitality business.',
      product: 'Product',
      company: 'Company',
      legal: 'Legal',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      copyright: '© 2025 Supervisor. All rights reserved.'
    },
    // CTA Section
    cta: {
      title: 'Ready to Take Control?',
      subtitle: 'Start your free trial today. No credit card required.',
      button: 'Start Free Trial'
    },
    // Cookie Banner
    cookie: {
      message: 'We respect your privacy. This website does not use tracking cookies.',
      accept: 'Got it',
      learnMore: 'Privacy Policy'
    }
  },
  nl: {
    // Navigation
    nav: {
      home: 'Home',
      features: 'Functies',
      pricing: 'Prijzen',
      about: 'Over ons',
      contact: 'Contact',
      getStarted: 'Aan de slag',
      login: 'Inloggen'
    },
    // Hero Section
    hero: {
      title: 'Neem de Controle over Uw Bedrijf',
      subtitle: 'Het complete besturingssysteem voor je horeca bedrijf. Stroomlijn operaties, beheers de dagelijkse taken, manage de marges en versterk je team.',
      cta: 'Start Gratis Proefperiode',
      secondaryCta: 'Bekijk Functies',
      tagline: 'Beheers de chaos in de horeca'
    },
    // Features Overview
    featuresOverview: {
      title: 'Alles wat U Nodig Heeft om Uw Bedrijf te Runnen',
      subtitle: 'Van dagelijkse taken tot controle over de marges, Supervisor geeft je volledige controle waardoor jij bezig kan zijn met het runnen van je bedrijf.',
      operations: {
        title: 'Operaties',
        description: 'Stroomlijn dagelijkse taken met digitale checklists, HACCP-compliance, voorraadbeheer.',
        items: ['Digitale Checklists', 'HACCP Compliance', 'Voorraadbeheer', 'Dagelijkse Taken']
      },
      finance: {
        title: 'Financiën',
        description: 'Krijg volledig financieel overzicht met kassatellingen, margetracking, kostenberekeningen en omzetprognoses.',
        items: ['Kassatellingen', 'Winstmarges', 'Kostenberekeningen', 'Omzetprognose', 'Financiële Rapporten']
      },
      employees: {
        title: 'Personeelsbeheer',
        description: 'Beheer uw team effectief met rolgebaseerde rechten, beoordelingsgesprekken en taakopdrachten.',
        items: ['Rolgebaseerde Toegang', 'Beoordelingsgesprekken', 'Taakbeheer', 'Teamcommunicatie']
      },
      multiBranch: {
        title: 'Multi-Vestiging',
        description: 'Beheer meerdere locaties vanuit één dashboard. Perfect voor groeiende horecabedrijven.',
        items: ['Gecentraliseerd Dashboard', 'Vestigingsvergelijking', 'Uniforme Rapportage', 'Locatiebeheer']
      }
    },
    // Features Page
    features: {
      title: 'Krachtige Functies voor de Horeca',
      subtitle: 'Ontdek alle tools die Supervisor de perfecte partner maken voor uw bedrijf.',
      operations: {
        title: 'Operatiebeheer',
        description: 'Houd uw bedrijf soepel draaiende met uitgebreide operationele tools.',
        checklists: {
          title: 'Digitale Checklists',
          description: 'Maak opstart- en afsluitchecklists. Volg voltooiing en zorg dat niets wordt gemist.'
        },
        haccp: {
          title: 'HACCP Compliance',
          description: 'Blijf conform voedselveiligheidsvoorschriften. Log temperaturen, leveringen en hygiënecontroles.'
        },
        reservations: {
          title: 'Tafelreserveringen',
          description: 'Visueel plattegrondbeheer. Drag-and-drop tafellayout met realtime beschikbaarheid.'
        },
        stock: {
          title: 'Voorraadbeheer',
          description: 'Volg voorraadniveaus, beheer producten en maak voorraadlijsten voor eenvoudig tellen.'
        },
        dailyTasks: {
          title: 'Dagelijkse Taken',
          description: 'Wijs dagelijkse taken toe en volg ze. Ontvang meldingen voor openstaande items.'
        }
      },
      finance: {
        title: 'Financiële Controle',
        description: 'Volledig zicht op uw financiële prestaties.',
        cashCounts: {
          title: 'Kassatellingen',
          description: 'Dagelijkse kassatellingen met afwijkingstracking en historische vergelijking.'
        },
        margins: {
          title: 'Winstmarges',
          description: 'Volg personeelspercentages en winstmarges per serviceperiode.'
        },
        costs: {
          title: 'Kostenberekeningen',
          description: 'Bereken gerechtskosten, beheer kostenposten en optimaliseer prijzen.'
        },
        forecast: {
          title: 'Omzetprognose',
          description: 'Voorspel toekomstige inkomsten op basis van historische data en trends.'
        },
        products: {
          title: 'Productbeheer',
          description: 'Beheer uw productcatalogus, volg voorraad en monitor voorraadniveaus.'
        },
        costPrice: {
          title: 'Kostprijs Berekening',
          description: 'Bereken de werkelijke kostprijs van uw producten en gerechten voor optimale prijsstelling.'
        }
      },
      team: {
        title: 'Teambeheer',
        description: 'Versterk uw team terwijl u controle behoudt.',
        permissions: {
          title: 'Rolgebaseerde Rechten',
          description: 'Maak aangepaste rechtenprofielen. Bepaal wie wat ziet en wie wat kan doen.'
        },
        reviews: {
          title: 'Beoordelingsgesprekken',
          description: 'Volg prestaties van medewerkers met gestructureerde beoordelingssjablonen.'
        },
        communication: {
          title: 'Teamcommunicatie',
          description: 'Weekupdates, berichten en feedbackkanalen voor uw team.'
        }
      },
      technical: {
        title: 'Technische Excellentie',
        description: 'Gebouwd voor moderne horecabedrijven.',
        pwa: {
          title: 'Mobiel Klaar (PWA)',
          description: 'Werkt op elk apparaat. Installeer als app op telefoons en tablets.'
        },
        languages: {
          title: 'Meertalig',
          description: 'Beschikbaar in Nederlands, Engels en Duits. Eenvoudig te wisselen per gebruiker.'
        },
        multiBranch: {
          title: 'Multi-Vestiging',
          description: 'Beheer onbeperkt aantal locaties vanuit één account. Vergelijk en consolideer.'
        }
      }
    },
    // Pricing Page
    pricing: {
      title: 'Eenvoudige, Transparante Prijzen',
      subtitle: 'Kies het plan dat bij uw bedrijf past. Geen verborgen kosten.',
      monthly: 'per maand',
      freeTrial: '3 dagen gratis proefperiode inbegrepen',
      company: {
        title: 'Bedrijf',
        description: 'Voor bedrijven die hun eigen operaties beheren',
        price: '49,95',
        features: [
          'Volledige operatiesuite',
          'Complete financiële tools',
          'Personeelsbeheer',
          'Rechtenprofielen',
          'Meertalige ondersteuning',
          'PWA mobiele toegang',
          'E-mail ondersteuning'
        ],
        cta: 'Doorgaan'
      },
      branch: {
        title: 'Vestiging',
        description: 'Voor organisaties die meerdere bedrijven beheren',
        price: '29,95',
        features: [
          'Beheer meerdere bedrijven',
          'Eén account toegang',
          'Geen constant in-/uitloggen',
          'Gecentraliseerd overzicht',
          'Rapportage over bedrijven heen',
          'Mobiele toegang',
          'E-mail ondersteuning'
        ],
        cta: 'Doorgaan'
      },
      faq: {
        title: 'Veelgestelde Vragen',
        questions: [
          {
            q: 'Welke betaalmethoden accepteren jullie?',
            a: 'We accepteren alle gangbare creditcards en SEPA automatische incasso via onze beveiligde betalingsprovider Stripe.'
          },
          {
            q: 'Kan ik op elk moment opzeggen?',
            a: 'Ja, u kunt uw abonnement op elk moment opzeggen. Uw toegang blijft tot het einde van de factureringsperiode.'
          },
          {
            q: 'Is er een gratis proefperiode?',
            a: 'Ja! Alle plannen komen met een 3-daagse gratis proefperiode. Geen creditcard nodig om te starten.'
          },
          {
            q: 'Bieden jullie korting voor meerdere vestigingen?',
            a: 'Ja, neem contact op met ons salesteam voor volumekortingen bij 5+ vestigingen.'
          }
        ]
      }
    },
    // About Page
    about: {
      title: 'Over Supervisor',
      subtitle: 'Wij helpen horecabedrijven de controle te nemen sinds dag één.',
      story: {
        title: 'Ons Verhaal',
        content: 'Supervisor is ontstaan uit een simpele observatie: horecaondernemers besteden te veel tijd aan het managen van chaos en te weinig tijd aan het laten groeien van hun bedrijf. We zijn dit bedrijf gestart met één missie – bedrijfseigenaren de inzichten en tools geven die ze nodig hebben om echt te begrijpen wat er in hun bedrijf gebeurt. Daarom heten we Supervisor. Wij geloven dat je als bedrijf de controle verdient. Ons platform brengt operaties, financiën en teambeheer samen in één intuïtief dashboard, zodat u het overzicht krijgt dat u nodig heeft om betere beslissingen te nemen, sneller. Van een enkel restaurant tot een hotelketen, Supervisor schaalt mee met uw ambities.'
      },
      mission: {
        title: 'Onze Missie',
        content: 'Horecabedrijven voorzien van de tools en inzichten die ze nodig hebben om te floreren. Wij geloven dat technologie moet vereenvoudigen, niet compliceren. Elke functie die we bouwen is ontworpen om u tijd te besparen en duidelijkheid te geven.'
      },
      values: {
        title: 'Onze Waarden',
        items: [
          {
            title: 'Eenvoud',
            description: 'Krachtig hoeft niet ingewikkeld te betekenen. We ontwerpen voor gebruiksgemak.'
          },
          {
            title: 'Transparantie',
            description: 'Geen verborgen kosten, geen verrassingen. Wat u ziet is wat u krijgt.'
          },
          {
            title: 'Betrouwbaarheid',
            description: 'Uw bedrijf is van ons afhankelijk. We nemen die verantwoordelijkheid serieus.'
          },
          {
            title: 'Innovatie',
            description: 'We verbeteren continu op basis van echte feedback van echte bedrijven.'
          }
        ]
      },
      location: {
        title: 'Onze Locatie',
        address: 'Zaailand 106',
        city: '8911 BN Leeuwarden',
        country: 'Nederland'
      }
    },
    // Contact Page
    contact: {
      title: 'Neem Contact Op',
      subtitle: 'Heeft u vragen? We horen graag van u.',
      form: {
        name: 'Naam',
        email: 'E-mail',
        company: 'Bedrijf',
        subject: 'Onderwerp',
        message: 'Bericht',
        submit: 'Verstuur Bericht',
        sending: 'Versturen...',
        success: 'Bericht succesvol verzonden! We nemen snel contact met u op.',
        error: 'Bericht verzenden mislukt. Probeer opnieuw of mail ons direct.'
      },
      info: {
        title: 'Contactgegevens',
        email: 'E-mail',
        address: 'Adres',
        hours: 'Openingstijden',
        hoursValue: 'Maandag - Vrijdag: 9:00 - 17:00'
      }
    },
    // Testimonials
    testimonials: {
      title: 'Wat Onze Klanten Zeggen',
      items: [
        {
          quote: 'Supervisor heeft de manier waarop we onze restaurantketen beheren getransformeerd. Alleen al de financiële inzichten hebben ons duizenden bespaard.',
          author: 'Jan de Vries',
          role: 'Eigenaar, De Gouden Lepel',
          company: 'Restaurant Groep'
        },
        {
          quote: 'Eindelijk een systeem dat ons hele team kan gebruiken. De HACCP-compliance functies geven ons elke dag gemoedsrust.',
          author: 'Maria Schmidt',
          role: 'Operations Manager',
          company: 'Hotel de gouden leeuw'
        },
        {
          quote: 'Het multi-vestiging overzicht is precies wat we nodig hadden. Nu kan ik alle locaties in één oogopslag zien.',
          author: 'Thomas Bakker',
          role: 'CEO',
          company: 'Café Collectief'
        }
      ]
    },
    // Footer
    footer: {
      description: 'Het complete besturingssysteem voor je horeca bedrijf.',
      product: 'Product',
      company: 'Bedrijf',
      legal: 'Juridisch',
      privacy: 'Privacybeleid',
      terms: 'Algemene Voorwaarden',
      copyright: '© 2025 Supervisor. Alle rechten voorbehouden.'
    },
    // CTA Section
    cta: {
      title: 'Klaar om de Controle te Nemen?',
      subtitle: 'Start vandaag uw gratis proefperiode. Geen creditcard nodig.',
      button: 'Start Gratis Proefperiode'
    },
    // Cookie Banner
    cookie: {
      message: 'Wij respecteren uw privacy. Deze website gebruikt geen tracking cookies.',
      accept: 'Begrepen',
      learnMore: 'Privacybeleid'
    }
  },
  de: {
    // Navigation
    nav: {
      home: 'Startseite',
      features: 'Funktionen',
      pricing: 'Preise',
      about: 'Über uns',
      contact: 'Kontakt',
      getStarted: 'Loslegen',
      login: 'Anmelden'
    },
    // Hero Section
    hero: {
      title: 'Übernehmen Sie die Kontrolle über Ihr Unternehmen',
      subtitle: 'Das vollständige Betriebssystem für dein Gastronomieunternehmen. Optimiere Abläufe, verwalte tägliche Aufgaben, kontrolliere Margen und stärke dein Team.',
      cta: 'Kostenlose Testversion starten',
      secondaryCta: 'Funktionen ansehen',
      tagline: 'Beherrschen Sie das Chaos im Gastgewerbe'
    },
    // Features Overview
    featuresOverview: {
      title: 'Alles, was Sie für Ihr Unternehmen brauchen',
      subtitle: 'Von täglichen Aufgaben bis zur Kontrolle über die Margen – Supervisor gibt dir die volle Kontrolle, damit du dich auf das Führen deines Unternehmens konzentrieren kannst.',
      operations: {
        title: 'Betrieb',
        description: 'Optimieren Sie tägliche Aufgaben mit digitalen Checklisten, HACCP-Compliance, Tischreservierungen und Bestandsmanagement.',
        items: ['Digitale Checklisten', 'HACCP Compliance', 'Tischreservierungen', 'Bestandsmanagement', 'Tägliche Aufgaben']
      },
      finance: {
        title: 'Finanzen',
        description: 'Erhalten Sie vollständige Finanzübersicht mit Kassenzählungen, Margen-Tracking, Kostenkalkulationen und Umsatzprognosen.',
        items: ['Kassenzählungen', 'Gewinnmargen', 'Kostenkalkulationen', 'Umsatzprognose', 'Finanzberichte']
      },
      employees: {
        title: 'Mitarbeiterverwaltung',
        description: 'Verwalten Sie Ihr Team effektiv mit rollenbasierten Berechtigungen, Leistungsbewertungen und Aufgabenzuweisungen.',
        items: ['Rollenbasierter Zugang', 'Leistungsbewertungen', 'Aufgabenverwaltung', 'Teamkommunikation']
      },
      multiBranch: {
        title: 'Multi-Standort',
        description: 'Verwalten Sie mehrere Standorte von einem Dashboard aus. Perfekt für wachsende Gastgewerbebetriebe.',
        items: ['Zentrales Dashboard', 'Standortvergleich', 'Einheitliche Berichterstattung', 'Standortverwaltung']
      }
    },
    // Features Page
    features: {
      title: 'Leistungsstarke Funktionen für das Gastgewerbe',
      subtitle: 'Entdecken Sie alle Tools, die Supervisor zum perfekten Partner für Ihr Unternehmen machen.',
      operations: {
        title: 'Betriebsmanagement',
        description: 'Halten Sie Ihr Unternehmen mit umfassenden operativen Tools am Laufen.',
        checklists: {
          title: 'Digitale Checklisten',
          description: 'Erstellen Sie Start- und Abschluss-Checklisten. Verfolgen Sie die Fertigstellung und stellen Sie sicher, dass nichts übersehen wird.'
        },
        haccp: {
          title: 'HACCP Compliance',
          description: 'Bleiben Sie konform mit Lebensmittelsicherheitsvorschriften. Protokollieren Sie Temperaturen, Lieferungen und Hygienekontrollen.'
        },
        reservations: {
          title: 'Tischreservierungen',
          description: 'Visuelle Raumplan-Verwaltung. Drag-and-Drop Tischlayout mit Echtzeit-Verfügbarkeit.'
        },
        stock: {
          title: 'Bestandsmanagement',
          description: 'Verfolgen Sie Lagerbestände, verwalten Sie Produkte und erstellen Sie Bestandslisten für einfaches Zählen.'
        },
        dailyTasks: {
          title: 'Tägliche Aufgaben',
          description: 'Weisen Sie tägliche Aufgaben zu und verfolgen Sie diese. Erhalten Sie Benachrichtigungen für ausstehende Punkte.'
        }
      },
      finance: {
        title: 'Finanzkontrolle',
        description: 'Vollständige Transparenz über Ihre finanzielle Leistung.',
        cashCounts: {
          title: 'Kassenzählungen',
          description: 'Tägliche Kassenzählungen mit Abweichungsverfolgung und historischem Vergleich.'
        },
        margins: {
          title: 'Gewinnmargen',
          description: 'Verfolgen Sie Mitarbeiteranteile und Gewinnmargen pro Serviceperiode.'
        },
        costs: {
          title: 'Kostenkalkulationen',
          description: 'Berechnen Sie Gerichtskosten, verwalten Sie Kostenpositionen und optimieren Sie die Preisgestaltung.'
        },
        forecast: {
          title: 'Umsatzprognose',
          description: 'Prognostizieren Sie zukünftige Einnahmen basierend auf historischen Daten und Trends.'
        },
        products: {
          title: 'Produktverwaltung',
          description: 'Verwalten Sie Ihren Produktkatalog, verfolgen Sie den Bestand und überwachen Sie Lagerbestände.'
        },
        costPrice: {
          title: 'Selbstkostenberechnung',
          description: 'Berechnen Sie den wahren Selbstkostenpreis Ihrer Produkte und Gerichte für optimale Preisgestaltung.'
        }
      },
      team: {
        title: 'Teammanagement',
        description: 'Stärken Sie Ihr Team und behalten Sie die Kontrolle.',
        permissions: {
          title: 'Rollenbasierte Berechtigungen',
          description: 'Erstellen Sie benutzerdefinierte Berechtigungsprofile. Bestimmen Sie, wer was sieht und wer was tun kann.'
        },
        reviews: {
          title: 'Leistungsbewertungen',
          description: 'Verfolgen Sie die Mitarbeiterleistung mit strukturierten Bewertungsvorlagen.'
        },
        communication: {
          title: 'Teamkommunikation',
          description: 'Wöchentliche Updates, Nachrichten und Feedback-Kanäle für Ihr Team.'
        }
      },
      technical: {
        title: 'Technische Exzellenz',
        description: 'Entwickelt für moderne Gastgewerbebetriebe.',
        pwa: {
          title: 'Mobil bereit (PWA)',
          description: 'Funktioniert auf jedem Gerät. Als App auf Telefonen und Tablets installierbar.'
        },
        languages: {
          title: 'Mehrsprachig',
          description: 'Verfügbar in Niederländisch, Englisch und Deutsch. Einfach pro Benutzer umschaltbar.'
        },
        multiBranch: {
          title: 'Multi-Standort',
          description: 'Verwalten Sie unbegrenzte Standorte von einem Konto aus. Vergleichen und konsolidieren.'
        }
      }
    },
    // Pricing Page
    pricing: {
      title: 'Einfache, transparente Preise',
      subtitle: 'Wählen Sie den Plan, der zu Ihrem Unternehmen passt. Keine versteckten Gebühren.',
      monthly: 'pro Monat',
      freeTrial: '3-tägige kostenlose Testversion inklusive',
      company: {
        title: 'Unternehmen',
        description: 'Für Unternehmen, die ihre eigenen Abläufe verwalten',
        price: '49,95',
        features: [
          'Vollständige Betriebs-Suite',
          'Komplette Finanztools',
          'Mitarbeiterverwaltung',
          'Berechtigungsprofile',
          'Mehrsprachige Unterstützung',
          'PWA mobiler Zugang',
          'E-Mail-Support'
        ],
        cta: 'Weiter'
      },
      branch: {
        title: 'Filiale',
        description: 'Für Organisationen, die mehrere Unternehmen verwalten',
        price: '29,95',
        features: [
          'Mehrere Unternehmen verwalten',
          'Ein Konto Zugang',
          'Kein ständiges An-/Abmelden',
          'Zentralisierte Übersicht',
          'Unternehmensübergreifende Berichte',
          'Mobiler Zugang',
          'E-Mail-Support'
        ],
        cta: 'Weiter'
      },
      faq: {
        title: 'Häufig gestellte Fragen',
        questions: [
          {
            q: 'Welche Zahlungsmethoden akzeptieren Sie?',
            a: 'Wir akzeptieren alle gängigen Kreditkarten und SEPA-Lastschrift über unseren sicheren Zahlungsanbieter Stripe.'
          },
          {
            q: 'Kann ich jederzeit kündigen?',
            a: 'Ja, Sie können Ihr Abonnement jederzeit kündigen. Ihr Zugang bleibt bis zum Ende des Abrechnungszeitraums bestehen.'
          },
          {
            q: 'Gibt es eine kostenlose Testversion?',
            a: 'Ja! Alle Pläne beinhalten eine 3-tägige kostenlose Testversion. Keine Kreditkarte erforderlich.'
          },
          {
            q: 'Bieten Sie Rabatte für mehrere Filialen?',
            a: 'Ja, kontaktieren Sie unser Vertriebsteam für Mengenrabatte ab 5+ Filialen.'
          }
        ]
      }
    },
    // About Page
    about: {
      title: 'Über Supervisor',
      subtitle: 'Wir helfen Gastgewerbebetrieben seit dem ersten Tag, die Kontrolle zu übernehmen.',
      story: {
        title: 'Unsere Geschichte',
        content: 'Supervisor entstand aus einer einfachen Beobachtung: Gastgewerbeunternehmer verbringen zu viel Zeit damit, Chaos zu managen, und zu wenig Zeit damit, ihr Geschäft zu entwickeln. Wir haben dieses Unternehmen mit einer Mission gegründet – Geschäftsinhabern die Einblicke und Tools zu geben, die sie brauchen, um wirklich zu verstehen, was in ihrem Unternehmen passiert. Deshalb heißen wir Supervisor. Wir glauben, dass Sie als Unternehmen die Kontrolle verdienen. Unsere Plattform vereint Betrieb, Finanzen und Teammanagement in einem intuitiven Dashboard und gibt Ihnen den Überblick, den Sie brauchen, um bessere Entscheidungen schneller zu treffen. Vom einzelnen Restaurant bis zur Hotelkette – Supervisor wächst mit Ihren Ambitionen.'
      },
      mission: {
        title: 'Unsere Mission',
        content: 'Gastgewerbebetriebe mit den Tools und Einblicken auszustatten, die sie zum Erfolg brauchen. Wir glauben, dass Technologie vereinfachen sollte, nicht verkomplizieren. Jede Funktion, die wir entwickeln, ist darauf ausgelegt, Ihnen Zeit zu sparen und Klarheit zu schaffen.'
      },
      values: {
        title: 'Unsere Werte',
        items: [
          {
            title: 'Einfachheit',
            description: 'Leistungsstark muss nicht kompliziert bedeuten. Wir gestalten für Benutzerfreundlichkeit.'
          },
          {
            title: 'Transparenz',
            description: 'Keine versteckten Gebühren, keine Überraschungen. Was Sie sehen, ist was Sie bekommen.'
          },
          {
            title: 'Zuverlässigkeit',
            description: 'Ihr Unternehmen hängt von uns ab. Wir nehmen diese Verantwortung ernst.'
          },
          {
            title: 'Innovation',
            description: 'Wir verbessern uns kontinuierlich basierend auf echtem Feedback von echten Unternehmen.'
          }
        ]
      },
      location: {
        title: 'Unser Standort',
        address: 'Zaailand 106',
        city: '8911 BN Leeuwarden',
        country: 'Niederlande'
      }
    },
    // Contact Page
    contact: {
      title: 'Kontakt aufnehmen',
      subtitle: 'Haben Sie Fragen? Wir freuen uns von Ihnen zu hören.',
      form: {
        name: 'Name',
        email: 'E-Mail',
        company: 'Unternehmen',
        subject: 'Betreff',
        message: 'Nachricht',
        submit: 'Nachricht senden',
        sending: 'Wird gesendet...',
        success: 'Nachricht erfolgreich gesendet! Wir melden uns bald bei Ihnen.',
        error: 'Nachricht konnte nicht gesendet werden. Bitte versuchen Sie es erneut oder mailen Sie uns direkt.'
      },
      info: {
        title: 'Kontaktinformationen',
        email: 'E-Mail',
        address: 'Adresse',
        hours: 'Geschäftszeiten',
        hoursValue: 'Montag - Freitag: 9:00 - 17:00'
      }
    },
    // Testimonials
    testimonials: {
      title: 'Was unsere Kunden sagen',
      items: [
        {
          quote: 'Supervisor hat die Art und Weise, wie wir unsere Restaurantkette führen, transformiert. Allein die finanziellen Einblicke haben uns Tausende gespart.',
          author: 'Jan de Vries',
          role: 'Inhaber, De Gouden Lepel',
          company: 'Restaurant Gruppe'
        },
        {
          quote: 'Endlich ein System, das unser gesamtes Team nutzen kann. Die HACCP-Compliance-Funktionen geben uns jeden Tag Sicherheit.',
          author: 'Maria Schmidt',
          role: 'Operations Manager',
          company: 'Hotel de gouden leeuw'
        },
        {
          quote: 'Die Multi-Standort-Übersicht ist genau das, was wir brauchten. Jetzt kann ich alle Standorte auf einen Blick sehen.',
          author: 'Thomas Bakker',
          role: 'CEO',
          company: 'Café Collectief'
        }
      ]
    },
    // Footer
    footer: {
      description: 'Das vollständige Betriebssystem für dein Gastronomieunternehmen.',
      product: 'Produkt',
      company: 'Unternehmen',
      legal: 'Rechtliches',
      privacy: 'Datenschutzrichtlinie',
      terms: 'Nutzungsbedingungen',
      copyright: '© 2025 Supervisor. Alle Rechte vorbehalten.'
    },
    // CTA Section
    cta: {
      title: 'Bereit, die Kontrolle zu übernehmen?',
      subtitle: 'Starten Sie noch heute Ihre kostenlose Testversion. Keine Kreditkarte erforderlich.',
      button: 'Kostenlose Testversion starten'
    },
    // Cookie Banner
    cookie: {
      message: 'Wir respektieren Ihre Privatsphäre. Diese Website verwendet keine Tracking-Cookies.',
      accept: 'Verstanden',
      learnMore: 'Datenschutzrichtlinie'
    }
  }
};

export default marketingTranslations;